import { Component, OnInit, effect, inject, input, signal } from '@angular/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { DataSeries } from 'src/app/models/classes/data-series.class';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CYAN, GROSVENOR_BLUE } from '@gcm/gcm-ui-angular';
import { HighchartsService } from 'src/app/services/highcharts-service.service';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';

@Component({
    selector: 'app-key-performance-widget',
    imports: [MatTabsModule, BaseWidgetComponent, GcmChartComponent, TourMatMenu],
    templateUrl: './key-performance-widget.component.html',
    styleUrls: ['./key-performance-widget.component.scss']
})
export class KeyPerformanceWidgetComponent implements OnInit {
  private highChartsService = inject(HighchartsService);
  private currencyFormat = inject(CurrencyFormatPipe);
  private activityTracking = inject(ActivityTrackingService);

  readonly widgets = input.required<Record<string, KeyValuePair<string | number, number>[]>>();
  readonly currency = input.required<string>();
  keyPerformanceDataForQuarterTop!: DataSeries;
  keyPerformanceDataForQuarterSumTop = -1;
  keyPerformanceDataForQuarterBottom!: DataSeries;
  keyPerformanceDataForQuarterSumBottom = -1;
  keyPerformanceQuarterChartOptionsTop = signal<Highcharts.Options | null>(null);
  keyPerformanceQuarterChartOptionsBottom = signal<Highcharts.Options | null>(null);
  kdpQuarterChartStyle: Record<string, string> = {};
  keyPerformanceDataSinceInceptionTop!: DataSeries;
  keyPerformanceDataSinceInceptionSumTop = -1;
  keyPerformanceSinceInceptionChartOptionsTop = signal<Highcharts.Options | null>(null);
  keyPerformanceDataSinceInceptionBottom!: DataSeries;
  keyPerformanceDataSinceInceptionSumBottom = -1;
  keyPerformanceSinceInceptionChartOptionsBottom = signal<Highcharts.Options | null>(null);
  kdpInceptionChartStyle: Record<string, string> = {};
  tabs = ['', 'Top Contributors', 'Bottom Contributors']

  buildKeyPerformanceChartOptions(showingChartForInception: boolean, top: boolean) {
    const currencyFormat = this.currencyFormat;
    const currency = this.currency();
    return {
      chart: {
        height: 360,
        marginRight: 0
      },
      plotOptions: {
        bar: {
          dataLabels: {
            formatter: function (this: Highcharts.Point) {
              return currencyFormat.transform(this.y, currency, 'Thousands', true, true, 0, true);
            }
          },
          enableMouseTracking: false
        }
      },
      tooltip: {
        enabled: false
      },
      series: [{
        custom: {
          showingChartForInception,
          top
        },
        color: GROSVENOR_BLUE,
        negativeColor: CYAN
      }]
    }
  }

  ngOnInit(): void {
    this.kdpQuarterChartStyle = this.kdpInceptionChartStyle = { width: '99%', display: 'flex' };
  }

  private readonly rebuildKeyPerformance = effect(() => {
    const widgets = this.widgets();
    if (widgets) {
      const topContributorsError = 'No Top Contributors';
      const bottomContributorsError = 'No Bottom Contributors';
      const noDataError = 'No Data to Report';
      this.keyPerformanceDataForQuarterTop = this.highChartsService.buildDataSeries(widgets['performanceQtdTopFive'], 'Market Value Change', widgets['performanceQtdBottomFive'].length == 0 ? noDataError : topContributorsError);
      this.keyPerformanceDataForQuarterBottom = this.highChartsService.buildDataSeries(widgets['performanceQtdBottomFive'], 'Market Value Change', widgets['performanceQtdTopFive'].length == 0 ? noDataError : bottomContributorsError);
      this.keyPerformanceDataSinceInceptionTop = this.highChartsService.buildDataSeries(widgets['performanceItdTopFive'], 'Market Value Change', widgets['performanceItdBottomFive'].length == 0 ? noDataError : topContributorsError);
      this.keyPerformanceDataSinceInceptionBottom = this.highChartsService.buildDataSeries(widgets['performanceItdBottomFive'], 'Market Value Change', widgets['performanceItdTopFive'].length == 0 ? noDataError : bottomContributorsError);
      if (widgets['performanceQtdTopFive'].length > 0) { this.buildTopQuarter(); }
      if (widgets['performanceQtdBottomFive'].length > 0) { this.buildBottomQuarter(); }
      if (widgets['performanceItdTopFive'].length > 0) { this.buildTopInception(); }
      if (widgets['performanceItdBottomFive'].length > 0) { this.buildBottomInception(); }
    }
  });

  handleChange(event: MatTabChangeEvent) {
    this.activityTracking.widgetTabChange('Key performance widget', this.tabs[event.index])
  }

  buildTopQuarter() {
    this.keyPerformanceDataForQuarterSumTop = this.keyPerformanceDataForQuarterTop.data.map(a => a.value).reduce((total, val) => total + Math.abs(val), 0);
    this.keyPerformanceQuarterChartOptionsTop.set(this.buildKeyPerformanceChartOptions(false, true));
  }

  buildBottomQuarter() {
    this.keyPerformanceDataForQuarterSumBottom = this.keyPerformanceDataForQuarterBottom.data.map(a => a.value).reduce((total, val) => total + Math.abs(val), 0);
    this.keyPerformanceQuarterChartOptionsBottom.set(this.buildKeyPerformanceChartOptions(false, false));
  }

  buildTopInception() {
    this.keyPerformanceDataSinceInceptionSumTop = this.keyPerformanceDataSinceInceptionTop.data.map(a => a.value).reduce((total, val) => total + Math.abs(val), 0);
    this.keyPerformanceSinceInceptionChartOptionsTop.set(this.buildKeyPerformanceChartOptions(true, true));
  }

  buildBottomInception() {
    this.keyPerformanceDataSinceInceptionSumBottom = this.keyPerformanceDataSinceInceptionBottom.data.map(a => a.value).reduce((total, val) => total + Math.abs(val), 0);
    this.keyPerformanceSinceInceptionChartOptionsBottom.set(this.buildKeyPerformanceChartOptions(true, false));
  }
}

